/**
 * Single-source-of-truth domain URLs per environment. Re-exported from
 * the root `domains.json` so every consumer (CORS allow-list, marketing
 * CTA URLs, connectors callback origin, Playwright baseURL, scripts that
 * print "the portal is at X") imports from here instead of hardcoding
 * strings.
 *
 * Editing the values in this module is forbidden — update `domains.json`
 * at the repo root.
 */
import domainsJson from '../../../../domains.json' with { type: 'json' };
import { z } from 'zod';
const EnvDomains = z.object({
    portal: z.string().nullable(),
    marketing: z.string().nullable(),
    api: z.string().nullable(),
    connectorsCallback: z.string().nullable(),
    contactEmail: z.string().nullable().optional(),
});
const DomainsConfig = z.object({
    version: z.string(),
    environments: z.object({
        local: EnvDomains,
        staging: EnvDomains.passthrough(),
        production: EnvDomains,
    }),
});
const parsed = DomainsConfig.parse(domainsJson);
/** Full per-env domain map. */
export const DOMAINS = Object.freeze(parsed.environments);
/**
 * Convenience accessor — returns the URL for a named service in a given
 * environment. Throws if staging is requested but not yet provisioned
 * (the JSON has `null` placeholders until the staging Firebase project
 * lands; callers should fail loudly rather than fall through to local).
 */
export function domainFor(env, service) {
    const value = DOMAINS[env][service];
    if (value === null || value === undefined) {
        throw new Error(`domainFor: domains.json has no ${service} URL for environment "${env}". ` +
            `Fill it in at the repo root before deploying to this env.`);
    }
    return value;
}
/**
 * Resolve the current runtime environment.
 *
 * Convention:
 *   - `NODE_ENV === 'production'` AND no emulator host → `production`
 *   - `FIRESTORE_EMULATOR_HOST` or `VITE_USE_FIREBASE_EMULATOR` truthy → `local`
 *   - Otherwise default to `local` (safest for dev — never leaks prod URLs).
 *
 * The browser side uses the Vite-injected `VITE_USE_FIREBASE_EMULATOR`
 * env var; the Node side uses the `FIRESTORE_EMULATOR_HOST` env (set by
 * the Functions emulator). Staging detection happens via an explicit
 * `LEANOPX_ENV=staging` env var when deployed to that env.
 */
export function currentEnvironment() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const env = globalThis.process?.env ?? {};
    const leanopxEnv = env['LEANOPX_ENV'];
    if (leanopxEnv === 'staging')
        return 'staging';
    if (leanopxEnv === 'production')
        return 'production';
    if (env['FIRESTORE_EMULATOR_HOST'])
        return 'local';
    if (env['NODE_ENV'] === 'production')
        return 'production';
    return 'local';
}
